/**
 * Prebid display wrapper split test.
 *
 * Shared by mai-publisher-split.js, which decides the split inline in <head>
 * before the CMP loads, and mai-publisher-ads.js, which decides it itself only
 * when that script did not run. One implementation, so both agree.
 *
 * @since TBD
 */

/**
 * Prebid display wrapper split test.
 *
 * Picks exactly one display prebid wrapper per visitor (or none = GAM Only),
 * sticky for 1 hour via the `maipub_pb_split` cookie. Resolution order:
 * #force hash > sticky cookie > weighted random.
 *
 * @since TBD
 *
 * @param {Array}    wrappers Array of { key, label, percent } from maiPubAdsVars.
 * @param {Function} log      Called with debug messages, like maiPubLog.
 *
 * @return {object|null} The winning { key, label }, or null for no wrapper.
 */
export function maiPubGetPrebidSplit( wrappers, log = () => {} ) {
	const cookieName = 'maipub_pb_split';

	// Create a signature of the current settings (e.g., "magnite:100" or "magnite:50,catalyst:50")
	const currentSig = wrappers.map(w => `${w.key}:${w.percent}`).join(',');

	// 1. Force a bucket via URL hash, e.g. #forcemagnite or #forcenone.
	if ( window.location.hash ) {
		if ( '#forcenone' === window.location.hash ) {
			maiPubStorePrebidSplit( cookieName, { key: 'none', label: 'none' }, currentSig );
			log( 'Prebid split: forced to no wrapper via #forcenone' );
			return null;
		}

		for ( const wrapper of wrappers ) {
			if ( `#force${wrapper.key}` === window.location.hash ) {
				const forced = { key: wrapper.key, label: wrapper.label };
				maiPubStorePrebidSplit( cookieName, forced, currentSig );
				log( 'Prebid split: forced via hash:', forced );
				return forced;
			}
		}
	}

	// 2. Reuse the sticky cookie IF it matches our current backend settings.
	const cookieMatch = document.cookie.match( /maipub_pb_split=([^;]+)/ );

	if ( cookieMatch ) {
		try {
			const stored = JSON.parse( decodeURIComponent( cookieMatch[1] ) );

			// Verify the configuration hasn't changed since the cookie was set
			if ( stored && stored.sig === currentSig ) {
				if ( 'none' === stored.key ) {
					return null;
				}

				const valid = wrappers.find( wrapper => wrapper.key === stored.key );

				if ( valid ) {
					return { key: valid.key, label: valid.label };
				}
			} else {
				log( 'Prebid split: Backend settings changed. Ignoring stale cookie.' );
			}
		} catch ( e ) {
			log( 'Prebid split: cookie parse error:', e );
		}
	}

	// 3. Weighted random over a 0-100 range. Any unallocated remainder
	//    (when percentages total under 100) falls through to no wrapper.
	const roll = Math.random() * 100;
	let   cumulative = 0;
	let   winner     = null;

	for ( const wrapper of wrappers ) {
		cumulative += Math.max( 0, Number( wrapper.percent ) || 0 );

		if ( roll < cumulative ) {
			winner = { key: wrapper.key, label: wrapper.label };
			break;
		}
	}

	maiPubStorePrebidSplit( cookieName, winner || { key: 'none', label: 'none' }, currentSig );

	return winner;
}

/**
 * Store the prebid split result in a 1-hour cookie so the visitor stays in the
 * same bucket across page loads.
 *
 * @since TBD
 *
 * @param {string} name The cookie name.
 * @param {object} data The { key, label } result to store.
 * @param {string} signature The current configuration signature string.
 *
 * @return {void}
 */
function maiPubStorePrebidSplit( name, data, signature ) {
	const maxAge = 60 * 60; // 1 hour, matching Mai_Publisher_Split_Testing.
	const secure = ( 'https:' === location.protocol ) ? '; Secure' : '';

	// Append the signature to the payload so we can validate it later
	data.sig = signature;

	document.cookie = `${name}=${encodeURIComponent( JSON.stringify( data ) )}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}
